<script lang="ts">
  /**
   * The status filter (spec 9.5, 9.6): an unlabelled multi-select beside the provider select,
   * reading "All statuses", up to two labels, or "N selected". Its options are "All statuses" and
   * then every status in attention order, each with its count over the rows the cards' union
   * counts. A tick applies at once. Nothing ticked is all statuses, so "All statuses" is ticked
   * then; picking it clears the rest, and ticking every status collapses back to it. On a failed
   * status read there is nothing to filter or count, so the select is disabled and shows no counts.
   *
   * It is the `$ui/select` multi-select the SDD category filter uses (`SensitiveDataTree`).
   */
  import * as Select from "$ui/select";

  import { provisionalSuffix } from "../exposedSensitiveDocuments";
  import { inAttentionOrder, STATUS_LABELS, type RowStatus, type StatusCount } from "./status";

  let {
    selected,
    counts,
    provisional = false,
    disabled = false,
    onChange,
  }: {
    selected: readonly RowStatus[];
    counts: readonly StatusCount[];
    /** The rows are still loading, so each count is only the rows so far (#2265). */
    provisional?: boolean;
    disabled?: boolean;
    onChange: (next: RowStatus[]) => void;
  } = $props();

  const ALL = "all";
  const ALL_LABEL = "All statuses";

  /** `selected` in attention order: what the trigger summarises and the options tick. */
  const selectedInOrder = $derived(inAttentionOrder(selected));
  const isAll = $derived(selectedInOrder.length === 0);
  const total = $derived(counts.reduce((sum, { count }) => sum + count, 0));
  const summary = $derived(
    isAll
      ? ALL_LABEL
      : selectedInOrder.length <= 2
        ? selectedInOrder.map((status) => STATUS_LABELS[status]).join(", ")
        : `${selectedInOrder.length} selected`,
  );

  const change = (values: string[]) => {
    const statuses = values.filter((value) => value !== ALL) as RowStatus[];
    const pickedAll = !isAll && values.includes(ALL);
    const everyStatus = statuses.length === counts.length;
    onChange(pickedAll || everyStatus ? [] : inAttentionOrder(statuses));
  };
</script>

<div class="w-48">
  <Select.Root
    multiple
    {disabled}
    selected={isAll
      ? [{ value: ALL, label: ALL_LABEL }]
      : selectedInOrder.map((status) => ({ value: status, label: STATUS_LABELS[status] }))}
    onSelectedChange={(next) => change((next ?? []).map((item) => item.value))}
  >
    <Select.Trigger name="status" aria-label="Status">
      <span class="truncate">{summary}</span>
    </Select.Trigger>
    <Select.Content sameWidth={true}>
      <Select.Group>
        <Select.Item value={ALL} label={ALL_LABEL} isSelected={isAll}>
          <span class="flex-1">{ALL_LABEL}</span>
          {#if !disabled}
            <span class="text-xs tabular-nums text-gray-400" data-testid="status-option-count"
              >{total}{provisionalSuffix(provisional)}</span
            >
          {/if}
        </Select.Item>
        <Select.Separator />
        {#each counts as { status, count } (status)}
          <Select.Item value={status} label={STATUS_LABELS[status]} isSelected={selectedInOrder.includes(status)}>
            <span class="flex-1">{STATUS_LABELS[status]}</span>
            {#if !disabled}
              <span class="text-xs tabular-nums text-gray-400" data-testid="status-option-count"
                >{count}{provisionalSuffix(provisional)}</span
              >
            {/if}
          </Select.Item>
        {/each}
      </Select.Group>
    </Select.Content>
  </Select.Root>
</div>
